/**
 * FreedrIQ — pre/post learning assessment for Freeze Dry Academy.
 *
 * Free tier ships two sections:
 *   1. Starting Self-Assessment — 7 Likert (1-5) questions → Confidence baseline.
 *   2. FreezeDryIQ Test         — 25 technical questions   → FreezeDryIQ Score.
 *
 * The two metrics stay separate. Confidence never influences the score.
 *
 * Route: /freedriq?tier=free&phase=pre (or ?phase=post)
 * Backend contract:
 *   GET  /api/freedriq/config?tier=X&phase=Y   → { sections:[...], brand }
 *   POST /api/freedriq/submit                  → { score, technical, confidence, ... }
 *   GET  /api/freedriq/summary                 → cumulative
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import { Button } from '../components/ui/button';
import {
  Sparkles, Award, ArrowLeft, ArrowRight, CheckCircle2, Loader2,
  BookOpen, Users, HeartHandshake, GraduationCap, TrendingUp,
} from 'lucide-react';
import StudyPlanCard from '../components/freedriq/StudyPlanCard';
import FreedrIQIntroModal from '../components/FreedrIQIntroModal';
import AskExpertButton from '../components/AskExpertButton';

const scoreBand = (s) => {
  if (s >= 85) return { label: 'FreezeDry Expert',     color: 'text-primary'      };
  if (s >= 70) return { label: 'Confident Practitioner', color: 'text-primary'    };
  if (s >= 50) return { label: 'Emerging Operator',    color: 'text-amber-600'    };
  if (s >= 25) return { label: 'Curious Learner',      color: 'text-amber-600'    };
  return          { label: 'Fresh Start',              color: 'text-emerald-600' };
};

const confidenceBand = (s) => {
  if (s >= 75) return 'Feels highly confident';
  if (s >= 50) return 'Feels moderately confident';
  if (s >= 25) return 'Feels a little confident';
  return 'New to freeze drying';
};

const FreedrIQ = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tier  = params.get('tier')  || 'free';
  const phase = params.get('phase') || 'pre';
  const fromSignup = params.get('from') === 'signup' || params.get('from') === 'onboarding';
  // First-run onboarding path: after the assessment, drop the user into the
  // Pricing gateway (Free-to-start / access code / creator code) instead of
  // the dashboard. Everywhere else keeps its usual "back to dashboard" CTA.
  const nextAfterFinish = fromSignup ? '/pricing?from=signup' : '/dashboard';

  const [config, setConfig]         = useState(null);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [step, setStep]             = useState(0);              // question index within active section
  const [confidence, setConfidence] = useState({});             // { sc_fundamentals: 1..5 }
  const [technical, setTechnical]   = useState({});             // { t1: "B", t3: ["A","B",...] }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]         = useState(null);

  useEffect(() => {
    axios.get(`${API}/freedriq/config?tier=${tier}&phase=${phase}`)
      .then((r) => setConfig(r.data))
      .catch(() => toast.error('Could not load FreedrIQ questions'));
  }, [API, tier, phase]);

  const sections = config?.sections || [];
  const activeSection = sections[sectionIdx];
  const questions = activeSection?.questions || [];
  const q = questions[step];

  // Progress across BOTH sections.
  const totalQuestions = useMemo(() => sections.reduce((n, s) => n + (s.questions?.length || 0), 0), [sections]);
  const answeredCount = useMemo(() => {
    let n = 0;
    for (let i = 0; i < sectionIdx; i++) n += sections[i]?.questions?.length || 0;
    n += step;
    return n;
  }, [sectionIdx, step, sections]);
  const progress = totalQuestions ? Math.round(((answeredCount + (result ? totalQuestions - answeredCount : 0)) / totalQuestions) * 100) : 0;

  const isConfidenceSection = activeSection?.id === 'starting_confidence';
  const bucket = isConfidenceSection ? confidence : technical;

  const canProceed = useMemo(() => {
    if (!q) return false;
    const v = bucket[q.id];
    if (q.type === 'multi_select') return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== null && v !== '';
  }, [q, bucket]);

  const setAnswer = (v) => {
    if (isConfidenceSection) setConfidence((c) => ({ ...c, [q.id]: v }));
    else                     setTechnical((t) => ({ ...t, [q.id]: v }));
  };

  const toggleMulti = (val) => {
    const cur = Array.isArray(bucket[q.id]) ? bucket[q.id] : [];
    // The final "I'm not sure" option is always the last one — mutually
    // exclusive with any real selection.
    const lastVal = q.options[q.options.length - 1].value;
    if (val === lastVal) { setAnswer([lastVal]); return; }
    const next = cur.filter((x) => x !== lastVal);
    if (next.includes(val)) setAnswer(next.filter((x) => x !== val));
    else                    setAnswer([...next, val]);
  };

  const goNext = () => {
    if (step < questions.length - 1) { setStep((s) => s + 1); return; }
    // End of section → advance to next section if any
    if (sectionIdx < sections.length - 1) {
      setSectionIdx((i) => i + 1);
      setStep(0);
      return;
    }
    submit();
  };
  const goBack = () => {
    if (step > 0) { setStep((s) => s - 1); return; }
    if (sectionIdx > 0) {
      const prev = sectionIdx - 1;
      setSectionIdx(prev);
      setStep((sections[prev]?.questions?.length || 1) - 1);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // Build starting_confidence in section order (7 items).
      const confSection = sections.find((s) => s.id === 'starting_confidence');
      const starting_confidence = (confSection?.questions || []).map((qq) => Number(confidence[qq.id] || 0));
      // Build technical answers in section order.
      const techSection = sections.find((s) => s.id === 'technical');
      const technical_answers = (techSection?.questions || [])
        .filter((qq) => technical[qq.id] !== undefined)
        .map((qq) => ({ question_id: qq.id, answer: technical[qq.id] }));

      const { data } = await axios.post(`${API}/freedriq/submit`, {
        tier, phase, starting_confidence, technical_answers,
      });
      setResult(data);
      try {
        localStorage.setItem('freedriq:completed', '1');
        localStorage.setItem('freedriq:score', String(data.score ?? ''));
      } catch { /* ignore */ }
      toast.success(`FreezeDryIQ Score: ${data.score}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  if (!config) {
    return (
      <div className="min-h-screen bg-background">
        <ToolSelector />
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading FreedrIQ…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="freedriq-page">
      <ToolSelector />
      <FreedrIQIntroModal onSkip={() => navigate(fromSignup ? '/pricing?from=signup' : '/learning')} />
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-6">
        {/* Header */}
        <div className="mb-4">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {config.brand?.assessment_name || 'FreedrIQ'} · {tier} · {phase}
          </div>
          <h1 className="font-heading text-3xl font-extrabold text-foreground mt-1">
            {phase === 'pre'
              ? (tier === 'free' ? 'Your entry assessment' : `Your ${tier} assessment`)
              : 'Growth check-in'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
            {phase === 'pre'
              ? 'Two short sections — a self-assessment and a knowledge test — establish your FreezeDryIQ baseline.'
              : "See how far you've come. We'll compare this to your entry answers."}
          </p>
        </div>

        {/* Section chips */}
        {!result && (
          <div className="flex items-center gap-2 mb-4">
            {sections.map((s, i) => (
              <div
                key={s.id}
                data-testid={`freedriq-section-chip-${s.id}`}
                className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border transition-colors ${
                  i === sectionIdx
                    ? 'bg-primary text-primary-foreground border-primary'
                    : i < sectionIdx
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-muted text-muted-foreground border-transparent'
                }`}
              >
                {i < sectionIdx && <CheckCircle2 className="inline w-3 h-3 mr-1" />}
                {i + 1}. {s.title}
              </div>
            ))}
          </div>
        )}

        {/* Progress bar */}
        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden mb-6">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${progress}%` }}
            data-testid="freedriq-progress"
          />
        </div>

        {/* Result view */}
        {result ? (
          <ResultCard result={result} tier={tier} phase={phase} user={user} fromSignup={fromSignup} onFinish={() => navigate(nextAfterFinish)} />
        ) : q ? (
          <div className="pro-card p-6" data-testid={`freedriq-question-${q.id}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
              {activeSection.title} · Question {step + 1} of {questions.length}
            </div>
            <h2 className="font-heading text-xl font-extrabold text-foreground leading-tight">{q.title}</h2>
            {q.help && <p className="text-xs text-muted-foreground mt-1.5">{q.help}</p>}

            {/* Options */}
            <div className="mt-4 space-y-2">
              {q.type === 'multiple_choice' && q.options.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setAnswer(o.value)}
                  data-testid={`freedriq-option-${q.id}-${o.value}`}
                  className={`w-full text-left rounded-lg border p-3 transition-colors text-sm ${
                    bucket[q.id] === o.value
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border hover:border-primary/40 hover:bg-muted'
                  }`}
                >
                  <span className="font-bold text-primary mr-2">{o.value}.</span>{o.label}
                </button>
              ))}

              {q.type === 'multi_select' && q.options.map((o) => {
                const active = (bucket[q.id] || []).includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggleMulti(o.value)}
                    data-testid={`freedriq-option-${q.id}-${o.value}`}
                    className={`w-full text-left rounded-lg border p-3 transition-colors text-sm flex items-center gap-2.5 ${
                      active
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border hover:border-primary/40 hover:bg-muted'
                    }`}
                  >
                    <span className={`h-4 w-4 rounded border flex-shrink-0 flex items-center justify-center ${
                      active ? 'bg-primary border-primary text-primary-foreground' : 'border-input'
                    }`}>
                      {active && <CheckCircle2 className="w-3 h-3" />}
                    </span>
                    <span className="font-bold text-primary mr-1">{o.value}.</span>{o.label}
                  </button>
                );
              })}

              {q.type === 'likert' && (
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                  {q.options.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setAnswer(o.value)}
                      data-testid={`freedriq-option-${q.id}-${o.value}`}
                      className={`rounded-lg border p-3 text-center transition-colors ${
                        bucket[q.id] === o.value
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border hover:border-primary/40 hover:bg-muted'
                      }`}
                    >
                      <div className="font-heading text-lg font-extrabold">{o.value}</div>
                      <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{o.label}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Nav */}
            <div className="flex justify-between items-center mt-6">
              <Button
                variant="outline"
                onClick={goBack}
                disabled={(sectionIdx === 0 && step === 0) || submitting}
                className="rounded-full font-bold"
                data-testid="freedriq-back"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
              </Button>
              {(sectionIdx < sections.length - 1) || (step < questions.length - 1) ? (
                <Button
                  onClick={goNext}
                  disabled={!canProceed || submitting}
                  className="rounded-full font-bold"
                  data-testid="freedriq-next"
                >
                  Next <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={!canProceed || submitting}
                  className="rounded-full font-bold"
                  data-testid="freedriq-submit"
                >
                  {submitting
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Scoring…</>
                    : <>See my FreezeDryIQ Score <Sparkles className="w-3.5 h-3.5 ml-1.5" /></>}
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ResultCard = ({ result, tier, phase, user, fromSignup, onFinish }) => {
  const band = scoreBand(result.score || 0);
  const tech = result.technical || {};
  const conf = result.confidence || {};
  const catBreakdown = tech.category_breakdown || {};
  const catRows = Object.entries(catBreakdown).sort((a, b) => b[1].percent - a[1].percent);
  const strongest = catRows[0];
  const weakest   = catRows[catRows.length - 1];

  return (
    <div className="space-y-4" data-testid="freedriq-result">
      {/* Score hero */}
      <div className="pro-card p-6 relative overflow-hidden border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div className="eyebrow text-primary inline-flex items-center gap-1.5">
          <Award className="w-3 h-3" /> {tier} · {phase} · FreezeDryIQ Score
        </div>
        <div className="mt-3 flex items-baseline gap-3">
          <div className={`font-heading text-6xl font-extrabold ${band.color}`} data-testid="freedriq-score">
            {result.score}
          </div>
          <div className="text-sm text-muted-foreground font-bold uppercase tracking-widest">/ 100</div>
        </div>
        <div className="text-lg font-heading font-extrabold text-foreground mt-1">
          {band.label}
        </div>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-md">
          Based strictly on technical accuracy — weighted by difficulty and safety-criticality.
          Confidence is tracked separately below.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Correct</div>
            <div className="font-heading text-xl font-extrabold mt-0.5" data-testid="freedriq-correct">
              {tech.correct}/{tech.total}
            </div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Confidence baseline</div>
            <div className="font-heading text-xl font-extrabold mt-0.5" data-testid="freedriq-confidence">
              {conf.score ?? 0}/100
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{confidenceBand(conf.score ?? 0)}</div>
          </div>
        </div>
        <div className="mt-4">
          <AskExpertButton
            variant="chip"
            label="Explain my result"
            context={{
              type:  'freedriq_report',
              id:    `${tier}-${phase}`,
              title: `FreedrIQ ${tier} ${phase} result`,
              snippet:
                `Score ${result.score}/100 (${band.label}). Correct ${tech.correct}/${tech.total}. ` +
                `Confidence ${conf.score ?? 0}/100 (${confidenceBand(conf.score ?? 0)}). ` +
                (weakest ? `Weakest category: ${weakest.cat} (${weakest.pct}%).` : ''),
            }}
          />
        </div>
      </div>

      {/* Category breakdown */}
      {catRows.length > 0 && (
        <div className="pro-card p-6" data-testid="freedriq-category-breakdown">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <GraduationCap className="w-3 h-3" /> Mastery by category
          </div>
          <div className="space-y-2.5 mt-3">
            {catRows.map(([cat, data]) => (
              <div key={cat} data-testid={`freedriq-cat-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex justify-between items-center mb-1">
                  <div className="text-xs font-semibold">{cat}</div>
                  <div className="text-xs text-muted-foreground">{data.percent}%</div>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${data.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
          {strongest && weakest && strongest[0] !== weakest[0] && (
            <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
              Strongest area: <span className="font-bold text-foreground">{strongest[0]}</span>.
              Prof. FreedrIQ will focus early lessons on <span className="font-bold text-foreground">{weakest[0]}</span>.
            </p>
          )}
        </div>
      )}

      {/* Personalised study plan — Prof. FreedrIQ recommendations */}
      <StudyPlanCard />

      {/* Post assessment growth CTA */}
      {phase === 'post' && (
        <Link
          to={`/freedriq/growth/${tier}`}
          className="pro-card group p-4 hover:border-primary/50 transition-colors border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent block"
          data-testid="freedriq-see-growth"
        >
          <TrendingUp className="w-5 h-5 text-primary mb-2" />
          <div className="font-heading font-extrabold text-sm">See your growth report</div>
          <div className="text-xs text-muted-foreground mt-1">
            Compare this result to your entry FreedrIQ, category by category.
          </div>
        </Link>
      )}

      {/* Next steps */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link
          to="/learning?tab=courses"
          className="pro-card group p-4 hover:border-primary/50 transition-colors"
          data-testid="freedriq-start-learning"
        >
          <BookOpen className="w-5 h-5 text-primary mb-2" />
          <div className="font-heading font-extrabold text-sm">Start your first course</div>
          <div className="text-xs text-muted-foreground mt-1">
            Personalised recommendations based on your entry answers.
          </div>
        </Link>
        {tier === 'free' && (result.score ?? 0) >= 60 ? (
          <Link
            to="/freedriq?tier=pro&phase=pre"
            className="pro-card group p-4 hover:border-primary/50 transition-colors border-primary/40 bg-primary/5"
            data-testid="freedriq-try-pro"
          >
            <Sparkles className="w-5 h-5 text-primary mb-2" />
            <div className="font-heading font-extrabold text-sm">Ready for the Pro assessment?</div>
            <div className="text-xs text-muted-foreground mt-1">
              You scored {result.score}. Level up — the Pro test covers cycle design, troubleshooting and process monitoring.
            </div>
          </Link>
        ) : (
          <Link
            to="/network"
            className="pro-card group p-4 hover:border-primary/50 transition-colors"
          >
            <Users className="w-5 h-5 text-primary mb-2" />
            <div className="font-heading font-extrabold text-sm">Meet your peers</div>
            <div className="text-xs text-muted-foreground mt-1">
              Suggestions and study groups tuned to your skills.
            </div>
          </Link>
        )}
      </div>

      <div className="text-center pt-2 flex items-center justify-center gap-3">
        <Button variant="outline" onClick={onFinish} className="rounded-full font-bold" data-testid="freedriq-back-to-dashboard">
          {fromSignup ? 'Continue to plans' : 'Back to Dashboard'}
          {fromSignup && <ArrowRight className="ml-1.5 w-3 h-3" />}
        </Button>
        <Link to={`/u/${user?.id || 'me'}`} className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1">
          <HeartHandshake className="w-3 h-3" /> See it on my profile
        </Link>
      </div>
    </div>
  );
};

export default FreedrIQ;
